import {
  CONSULT_NOTE_SECTION_ORDER,
  type ConsultNoteFlash,
  type ConsultNoteFormAssessmentSection,
  type ConsultNoteFormPhysicalExamSection,
  type ConsultNoteFormSections,
  type ConsultNoteFormTextSection,
  type ConsultNoteSectionKey,
  type ConsultNoteSections,
  type ConsultNoteSignedAddendum,
  type ConsultNoteTextSectionKey,
  type ConsultNoteVisit,
} from "./types";

type BadgeTone = "neutral" | "emerald" | "sky" | "amber" | "rose";

const APP_BADGE_TONES: Record<BadgeTone, string> = {
  neutral: "border-neutral-200 bg-neutral-100/80 text-neutral-700 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300",
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-900/30 dark:text-emerald-300",
  sky: "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900/60 dark:bg-sky-900/30 dark:text-sky-300",
  amber: "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/60 dark:bg-amber-900/30 dark:text-amber-300",
  rose: "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/60 dark:bg-rose-900/30 dark:text-rose-300",
};

const TEXT_SECTION_KEYS = CONSULT_NOTE_SECTION_ORDER.filter(
  (key): key is ConsultNoteTextSectionKey => key !== "assessment" && key !== "physical_exam"
);

export function joinClasses(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(" ");
}

export function appBadgeClasses(tone: BadgeTone = "neutral") {
  return joinClasses("app-badge", APP_BADGE_TONES[tone]);
}

export function flashClasses(flash: ConsultNoteFlash | null) {
  const tone = flash?.alert ? "rose" : "emerald";

  return joinClasses(
    "rounded-xl border px-4 py-3 text-sm",
    tone === "rose"
      ? "border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-100"
      : "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-100"
  );
}

export function createFormSections(sections: ConsultNoteSections): ConsultNoteFormSections {
  const nextSections = {} as ConsultNoteFormSections;

  TEXT_SECTION_KEYS.forEach((sectionKey) => {
    const section = sections[sectionKey];
    nextSections[sectionKey] = {
      key: section.key,
      title: section.title,
      kind: "text",
      rows: section.rows,
      value: section.effectiveText,
      sourceRefs: section.sourceRefs.map((sourceRef) => ({ ...sourceRef })),
      imagingRows: section.imagingRows.map((row) => ({ ...row })),
    };
  });

  nextSections.assessment = {
    key: sections.assessment.key,
    title: sections.assessment.title,
    kind: "assessment",
    customText: sections.assessment.customText,
    options: sections.assessment.options.map((option) => ({ ...option })),
    sourceRefs: sections.assessment.sourceRefs.map((sourceRef) => ({ ...sourceRef })),
  };

  nextSections.physical_exam = {
    key: sections.physical_exam.key,
    title: sections.physical_exam.title,
    kind: "physical_exam",
    value: sections.physical_exam.effectiveText,
    defaultText: sections.physical_exam.defaultText,
    sourceRefs: sections.physical_exam.sourceRefs.map((sourceRef) => ({ ...sourceRef })),
  };

  return nextSections;
}

export function appendUniquePlanText(baseText: string, addition: string) {
  const normalizedBaseText = baseText.trim();
  const normalizedAddition = addition.trim();

  if (!normalizedAddition || normalizedBaseText.includes(normalizedAddition)) return normalizedBaseText;
  return normalizedBaseText ? `${normalizedBaseText}\n\n${normalizedAddition}` : normalizedAddition;
}

export function buildMutationPayload(title: string, sections: ConsultNoteFormSections, visit: ConsultNoteVisit) {
  const payload: {
    title: string;
    visit: { date: string; time?: string };
    sections: Record<string, { edited_text?: string; fields?: Record<string, string>; selected_keys?: string[]; custom_text?: string }>;
  } = {
    title,
    visit: {
      date: visit.date,
      ...(visit.time ? { time: visit.time } : {}),
    },
    sections: {},
  };

  TEXT_SECTION_KEYS.forEach((sectionKey) => {
    payload.sections[sectionKey] = { edited_text: sections[sectionKey].value };
  });

  payload.sections.physical_exam = {
    edited_text: sections.physical_exam.value,
  };

  payload.sections.assessment = {
    selected_keys: sections.assessment.options.filter((option) => option.selected).map((option) => option.key),
    custom_text: sections.assessment.customText,
  };

  return payload;
}

export function serializeMutationPayload(title: string, sections: ConsultNoteFormSections, visit: ConsultNoteVisit) {
  return JSON.stringify(buildMutationPayload(title, sections, visit));
}

export function buildMutationFormData(title: string, sections: ConsultNoteFormSections, visit: ConsultNoteVisit) {
  const formData = new FormData();

  formData.append("consult_note[title]", title);
  formData.append("consult_note[visit][date]", visit.date);
  if (visit.time) {
    formData.append("consult_note[visit][time]", visit.time);
  }

  TEXT_SECTION_KEYS.forEach((sectionKey) => {
    formData.append(`consult_note[sections][${sectionKey}][edited_text]`, sections[sectionKey].value);
  });

  formData.append("consult_note[sections][physical_exam][edited_text]", sections.physical_exam.value);

  sections.assessment.options.forEach((option) => {
    if (!option.selected) return;

    formData.append("consult_note[sections][assessment][selected_keys][]", option.key);
  });
  formData.append("consult_note[sections][assessment][custom_text]", sections.assessment.customText);

  return formData;
}

function normalizeNoteText(value: string) {
  return value.replace(/\r\n?/g, "\n").trim();
}

function buildAssessmentSectionText(section: ConsultNoteFormAssessmentSection) {
  return normalizeNoteText(
    [
      ...section.options.filter((option) => option.selected && option.key !== "custom").map((option) => option.label),
      section.customText,
    ]
      .filter(Boolean)
      .join("\n")
  );
}

function buildSectionCopyText(
  section: ConsultNoteFormTextSection | ConsultNoteFormPhysicalExamSection | ConsultNoteFormAssessmentSection
) {
  switch (section.kind) {
    case "assessment":
      return buildAssessmentSectionText(section);
    case "physical_exam":
      return normalizeNoteText(section.value);
    case "text":
      return normalizeNoteText(section.value);
    default:
      return "";
  }
}

export function buildNoteCopyText(
  title: string,
  visit: ConsultNoteVisit,
  sectionOrder: ConsultNoteSectionKey[],
  sections: ConsultNoteFormSections,
  signedAddendums: ConsultNoteSignedAddendum[] = []
) {
  const blocks = [
    normalizeNoteText(title),
    `DATE OF VISIT\n${[visit.date, visit.time].filter(Boolean).join(" ").trim()}`,
    ...sectionOrder.map((sectionKey) => {
      const section = sections[sectionKey];
      const text = buildSectionCopyText(section);
      if (sectionKey === "medical_necessity" && !text) return null;
      const heading = sectionKey === "medical_necessity" ? "Medical Necessity" : normalizeNoteText(section.title).toUpperCase();
      return `${heading}\n${text}`;
    }),
    signedAddendums.length > 0
      ? `ADDENDUM\n${signedAddendums
          .map((addendum) =>
            [normalizeNoteText(addendum.body), `Addended by: ${addendum.signedByName} on ${addendum.signedAtLabel}`]
              .filter(Boolean)
              .join("\n")
          )
          .join("\n\n")}`
      : null,
  ].filter(Boolean);

  return blocks.join("\n\n").trim();
}

export function noteStatusTone(finalized: boolean) {
  return finalized ? "emerald" : "amber";
}

export function flashMessage(flash: ConsultNoteFlash | null) {
  return flash?.alert || flash?.notice || "";
}

export function isJsonObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function isConsultNoteSectionKey(value: string): value is ConsultNoteSectionKey {
  return CONSULT_NOTE_SECTION_ORDER.includes(value as ConsultNoteSectionKey);
}
